import { components } from "@/api-schema";
import { RelocationOrderTransportMethod } from "@/api-schema-enums";
import { alphartisApiAxios } from "@/config/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RelocationOrderFlowRouteType } from "./_helpers/default-relocation-order-flow.enums";
import {
  RelocationOrderFlowInitBodyMap,
  resolveRelocationOrderFlowInitPath,
} from "./_helpers/resolve-relocation-order-flow-init-path";

const RELOCATION_ORDER_RESOURCE = "relocation-order" as const;

export type RelocationOrderFlowInitResponse = components["schemas"]["RelocationOrderResponseDto"];

/**
 * Generic mutation hook for relocation order flow init (order creation).
 *
 * The correct endpoint is resolved dynamically from the route type and transport method.
 * Request bodies are type-checked at compile time via {@link RelocationOrderFlowInitBodyMap}.
 *
 * @example
 * const { mutateAsync } = useRelocationOrderFlowInitMutation(
 *   RelocationOrderFlowRouteType.BranchToBranch,
 *   RelocationOrderTransportMethod.TransportCompany,
 * );
 * await mutateAsync({ vehicleUuid: "...", destinationBranchUuid: "...", ... });
 */
export const useRelocationOrderFlowInitMutation = <
  R extends RelocationOrderFlowRouteType,
  T extends RelocationOrderTransportMethod,
>(
  routeType: R,
  transportMethod: T,
) => {
  const queryClient = useQueryClient();

  return useMutation<RelocationOrderFlowInitResponse, Error, RelocationOrderFlowInitBodyMap[R][T]>({
    mutationFn: async (body: RelocationOrderFlowInitBodyMap[R][T]) => {
      const path = resolveRelocationOrderFlowInitPath(routeType, transportMethod);
      const response = await alphartisApiAxios.post<RelocationOrderFlowInitResponse>(path, body);

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [RELOCATION_ORDER_RESOURCE],
      });
    },
  });
};
